import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Phone, ChevronDown } from 'lucide-react';

const HeroSection = ({ isLoaded }) => {
  const { t } = useLanguage();

  return (
    <section 
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      aria-label="Hero section"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1763542010145-ac52bf257d7e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHx6ZW4lMjBnYXJkZW4lMjB3YXRlciUyMHJpcHBsZXMlMjBiYW1ib28lMjBncmVlbiUyMHBlYWNlZnVsJTIwbmF0dXJlJTIwbGFuZHNjYXBlJTIwaGlnaCUyMHF1YWxpdHl8ZW58MHx8fHwxNzcxMzAwMDUxfDA&ixlib=rb-4.1.0&q=85')`
        }}
        role="img"
        aria-label="Peaceful zen garden with bamboo and water"
      >
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-[#1C1C1E]/50"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 container-main text-center px-6">
        <div className={`max-w-4xl mx-auto ${isLoaded ? 'animate-fade-in' : 'opacity-0'}`}>
          {/* Clinic Name Badge */}
          <div 
            className={`inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-6 py-2 mb-8 ${isLoaded ? 'animate-fade-in' : 'opacity-0'}`}
            style={{ animationDelay: '0.1s' }}
          >
            <span className="text-white/90 font-body text-sm tracking-wider uppercase">
              Healing Clinic • 힐링한의원
            </span>
          </div>

          {/* Main Headline */}
          <h1 
            className={`font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight mb-6 ${isLoaded ? 'animate-fade-in' : 'opacity-0'}`}
            style={{ animationDelay: '0.2s' }}
            data-testid="hero-headline"
          >
            {t('hero.headline')}
          </h1>

          {/* Subheadline */}
          <p 
            className={`font-body text-base md:text-lg text-white/85 max-w-2xl mx-auto mb-10 leading-relaxed ${isLoaded ? 'animate-fade-in' : 'opacity-0'}`}
            style={{ animationDelay: '0.3s' }}
            data-testid="hero-subheadline"
          >
            {t('hero.subheadline')}
          </p>

          {/* CTA Button */}
          <div 
            className={`flex flex-col sm:flex-row items-center justify-center gap-4 ${isLoaded ? 'animate-fade-in' : 'opacity-0'}`}
            style={{ animationDelay: '0.4s' }}
          >
            <a 
              href="tel:+17145915360"
              className="group flex items-center gap-3 bg-sage hover:bg-sage-dark text-white rounded-full px-8 py-4 font-body font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-colors transition-shadow duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent"
              data-testid="hero-cta-button"
              aria-label={`${t('hero.cta')}: ${t('hero.phone')}`}
            >
              <Phone className="w-5 h-5 group-hover:animate-pulse" />
              <span>{t('hero.cta')}</span>
            </a>
            
            <span 
              className="text-white font-body font-bold text-xl tracking-wide"
              data-testid="hero-phone-number"
            >
              {t('hero.phone')}
            </span>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <a 
          href="#about"
          className="flex flex-col items-center text-white/70 hover:text-white transition-colors duration-200"
          aria-label="Scroll to learn more"
          data-testid="scroll-indicator"
        >
          <span className="font-body text-sm mb-2 tracking-wider uppercase">
            {useLanguage().language === 'en' ? 'Discover' : 'Khám phá'}
          </span>
          <ChevronDown className="w-6 h-6 scroll-indicator" />
        </a>
      </div>
    </section>
  );
};

export default HeroSection;
